import React, { useState } from 'react';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import type { SxProps, Theme } from '@mui/material';

export interface TapTooltipProps {
  /** Full text revealed by the tooltip. */
  title: string;
  /** Content the tooltip anchors to (typically truncated text). */
  children: React.ReactNode;
  /** Extra styles merged onto the wrapping span. */
  sx?: SxProps<Theme>;
}

/**
 * Tooltip that works on both pointer and touch devices: it opens on hover or
 * tap and dismisses on mouse-leave or tap-away. MUI's built-in touch handling
 * needs a long-press, so taps drive a controlled open state instead.
 */
const TapTooltip = ({ title, children, sx }: TapTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Tooltip
        title={title}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        disableTouchListener
      >
        <Box
          component="span"
          onClick={() => setIsOpen(true)}
          sx={[{ cursor: 'pointer' }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
          {children}
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default TapTooltip;
